/**
 * Jellyfish haircuts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_named_haircuts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const jellyfishHaircutsPack: ToilePack = {
    slug: 'jellyfish-haircuts',
    category: 'beauty',
    name: 'Jellyfish haircuts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'sleek', label: 'Sleek', subject: 'a sleek jellyfish cut with a chin-length bob layer over straight long hair'},
        {key: 'colored', label: 'Two-tone', subject: 'a jellyfish cut with the bob layer black and the long layer bleached'},
        {key: 'wavy', label: 'Wavy', subject: 'a wavy jellyfish cut with a textured bob layer'},
    ],
    styles: [
        {key: 'editorial', label: 'Editorial', style: 'a beauty editorial portrait against a painted plaster wall, soft even light from above and slightly frontal, a mid-tone balance with gentle shadow under the jaw', palette: 'pale grey-blue plaster, the hair in clear contrast', framing: 'near-frontal head-and-shoulders, centred with space above the crown'},
        {key: 'street', label: 'Street', style: 'photographed outdoors on a city street in low late-afternoon sun from camera-right grazing the hair, a medium balance with soft shadow on the far cheek', palette: 'warm concrete and shop windows soft in the distance', framing: 'three-quarter view at eye level, shallow depth of field'},
        {key: 'window', label: 'Window light', style: 'indoors beside a large window, side daylight from camera-left modelling the face, a soft balance with the far side of the face in gentle shadow', palette: 'white walls and pale wood, the hair the darkest tone', framing: 'three-quarter view, the room soft behind'},
    ],
    directions: ['pastel pink', 'curtain bangs', 'Y2K look'],
    directionHint: 'try: pastel pink',
    seo: {
        title: 'Jellyfish Haircuts: Sleek, Two-Tone, Wavy',
        metaDescription: 'Nine pictures of the jellyfish cut, a short bob layer over long lengths: sleek, two-tone and wavy versions in editorial, street and window light.',
        h1: 'Jellyfish haircuts: sleek, two-tone, wavy',
        tagline: 'Nine pictures, three jellyfish cuts, each in an editorial portrait, on a city street and by a window.',
        intro: 'The jellyfish cut is a short bob layer on top of long lengths. These pictures show a sleek jellyfish with a chin-length bob over straight hair, a two-tone version with a black bob layer and bleached long layer, and a wavy version with a textured bob. Each is seen in an editorial portrait, on a city street and by a window. You can type a change, such as pastel pink, to ask for the cut with it.',
        useCases: [
            'a reference for the bob layer',
            'choosing a sleek, two-tone or wavy jellyfish',
            'a wall print',
            'a profile picture',
        ],
        faq: [
            {q: 'Where is the bob layer?', a: 'It sits at the chin, over the long lengths below.'},
            {q: 'What is the two-tone version?', a: 'The bob layer is black and the long layer below it is bleached.'},
            {q: 'Can I change the colour?', a: 'Yes. Type the change and the picture is made with it. It is a request, so the result may differ from the sample.'},
        ],
    },
}
